import { ContentService } from '@blk/cwp-ng-framework';
import * as unzip from 'lodash/unzip';

import { CellData } from '../cell-data';
import { ColumnConfig } from '../column-config';
import { ColumnText } from '../column-text';
import { ChildMapperDirection } from '../enums';
import { ColumnExtractor } from '../extractors/column-extractor';
import { StoreColumnExtractor } from '../extractors/store-column-extractor';
import { SeriesMapper } from '../series-mappers/series-mapper.interface';
import { TableData } from '../table-data';
import { AsIsTableDataTransform } from '../table-data-transform/as-is-table-data-transform';
import { TableDataTransform } from '../table-data-transform/table-data-transform';
import { SeriesExtractionExtractorConfig } from './series-extractor-config';

// notes on combined portfolio grid extractor
// 1.category doesn't need to unique, if category is duplicated in data point, each value will be append to an array: Line 66-75

/** 
* childMapperDirection
* ParentToChild
* When used for a display table ParentToChild will render in the SimpleTableComponent
* Children will have the class sub-category applied and they will appear nested beneath parent categories using a margin-left
* Used by PA Equity Region Breakdown and Scenario Analysis
*
* ChildToParent
* When used for a display table ChildToParent will render in the NestedTableComponent
* Children will appear in hidden by default, but can be expanded using accordions
* Used for the CMA Asset Allocation Table
*/
export class CombinedPortfoliosGridExtractorConfig extends SeriesExtractionExtractorConfig {

	public static create(options: CombinedPortfoliosGridExtractorConfigOptions): CombinedPortfoliosGridExtractorConfig {
		return new this(
			options.mapper,
			options.firstColumnTitle,
			options.portfolioName,
			options.category,
			options.value,
			options.categorySortFunction,
			options.childMapper,
			options.childMapperDirection,
			options.childCategory,
			options.childValue,
			options.tableDataTransform,
			options.batchSize,
			options.portfolioFilter,
			options.firstColumnExtractor
		);
	}

	/**
	 * @deprecated Use CombinedPortfoliosGridExtractorConfig.create() instead
	 */
	constructor(
		mapper: SeriesMapper,
		public firstColumnTitle: ColumnText,
		portfolioName: ColumnConfig<StoreColumnExtractor>,
		public readonly category: ColumnConfig<ColumnExtractor>,
		public readonly value: ColumnConfig<ColumnExtractor>,
		public categorySortFunction?: (a, b) => number, // used by Array.prototype.sort(). leave empty for alphabetical sorting
		childMapper?: (parent: any) => any[],
		private childMapperDirection: ChildMapperDirection = ChildMapperDirection.ParentToChild,
		public readonly childCategory: ColumnConfig<ColumnExtractor> = category,
		public readonly childValue: ColumnConfig<ColumnExtractor> = value,
		tableDataTransform: TableDataTransform = AsIsTableDataTransform.instance,
		private batchSize: number = Number.MAX_VALUE,
		private portfolioFilter: Function = (port: any) => true,
		// firstColumnExtractor is an optional parameter which can be used when we want the value in the first column
		// to be different from the 'id' used to build the rows in the table (if it's not provided, the category column extractor will be used)
		private firstColumnExtractor: ColumnConfig<ColumnExtractor> = category

	) {
		super(portfolioName, mapper, childMapper, tableDataTransform);
	}

	public storeDependencies(): string[] {
		return this.mapper.getStoreDependencies();
	}

	public extract(storeLookup: Map<string, any[]>, contentSvc: ContentService): TableData[] {
		const dataArgs = this.mapper.getStoreDependencies().map(config => storeLookup.get(config));
		const filteredIndices = [];
		let i = 0;
		const portsDataArgs = unzip(dataArgs)
			.filter(data => {
				if (!this.portfolioFilter(data)) {
					filteredIndices.push(i);
					i++;
					return false;
				}
				i++;
				return true;
			});

		const filteredLookup = new Map();
		storeLookup.forEach((values: any[], key) => {
			filteredLookup.set(key, values.filter((v, i) => !filteredIndices.includes(i)));
		});

		const portfolioNames: CellData[] = this.extractStoreColumn(this.portfolioName, filteredLookup, contentSvc);
		const results = [];
		while (portsDataArgs.length > 0) {
			results.push(this.extractBatch(portsDataArgs.splice(0, this.batchSize), contentSvc, portfolioNames.splice(0, this.batchSize)));
		}
		return results;
	}

	private extractBatch(portsDataArgs: any[], contentSvc: ContentService, portfolioNames: CellData[]): TableData {
		const categoriesUniverse: Map<string, CellData> = new Map();
		const categoriesPerPortfolio: string[][] = [];
		const portfoliosValues: Map<string, CellData>[] = [];

		portsDataArgs.forEach((portfolioData, portNo) => {
			portfoliosValues.push(new Map());
			categoriesPerPortfolio.push([]);

			this.mapper.mapper(...portfolioData).forEach(data => {
				const category = this.category.extractor.extractor()(data);
				const firstColumnData = this.firstColumnExtractor.extractor.extractor()(data);
				const categoryCell = new CellData(
					this.firstColumnExtractor,
					firstColumnData,
					this.firstColumnExtractor.labelFormatter.format(firstColumnData, contentSvc),
					null,
					this.generateAttributes(firstColumnData, this.firstColumnExtractor, contentSvc),
					[]
				);
				categoriesPerPortfolio[portNo].push(category);

				if (!categoriesUniverse.has(category)) {
					categoriesUniverse.set(category, categoryCell);
				}

				const valueCell = this.extractDataColumn(this.value, data, contentSvc, null);
				const cellMap = portfoliosValues[portNo];
				if (!cellMap.has(category)) {
					// if category hasn't exist yet
					cellMap.set(category, valueCell);
				} else if (cellMap.has(category) && typeof cellMap.get(category).rawValue !== 'number') {
					// if existed but not number, append value to array
					if (cellMap.get(category).rawValue instanceof Array) {
						(<any[]>cellMap.get(category).rawValue).push(valueCell.rawValue);
					} else {
						(cellMap.get(category).rawValue = [cellMap.get(category).rawValue]).push(valueCell.rawValue);
					}
					cellMap.get(category).formattedValue = cellMap.get(category).formattedValue + ',' + cellMap.get(category).config.labelFormatter.format(valueCell.rawValue);
				} else {
					// if existed as number, combine value together
					const oldValue = cellMap.get(category);
					const aggValue = Number(oldValue.rawValue) + Number(valueCell.rawValue);
					cellMap.set(category, new CellData(oldValue.config, aggValue, oldValue.config.labelFormatter.format(aggValue), oldValue.parent, oldValue.attribute));
				}

				// parent array that's only added to the children when childMapperDirection === ChildMapperDirection.ParentToChild
				const parent = [categoryCell, valueCell, portfolioNames[portNo]];
				if (this.childMapper) {
					this.childMapper(data)?.forEach(childData => {
						switch (this.childMapperDirection) {
							case ChildMapperDirection.ParentToChild: {
								const categoryCell = this.extractDataColumn(this.childCategory, childData, contentSvc, parent);
								const valueCell = this.extractDataColumn(this.childValue, childData, contentSvc, parent);
								categoriesPerPortfolio[portNo].push(`${category} > ${categoryCell.rawValue}`);
								categoriesUniverse.set(`${category} > ${categoryCell.rawValue}`, categoryCell);
								portfoliosValues[portNo].set(`${category} > ${categoryCell.rawValue}`, valueCell);
								break;
							}
							case ChildMapperDirection.ChildToParent: {
								const categoryCell = this.extractDataColumn(this.childCategory, childData, contentSvc);
								const valueCell = this.extractDataColumn(this.childValue, childData, contentSvc);
								const parentCategory = categoriesUniverse.get(category);

								// Condition to check for unique subcategories
								// If there are multiple portfolios, subcategories appearing in both portfolios would get pushed to the children array which would cause duplicate subcategories to be added 
								// This would cause components to display incorrectly
								if (!parentCategory.children.find(subCategory => subCategory.rawValue === categoryCell.rawValue)) {
									// children are pushed in order of appearance while iterating through the portfolios
									parentCategory.children.push(categoryCell);
								}

								// Values per portfolio per subcategory are always unique, so we always need to push them
								// We push the value at the index of the subcategory in the categoriesUniverse to ensure the cell is in the correct position 
								// New subcategories are always pushed to the categories universe meaning that the subcategory order will not change! The index will always be correct
								const parentValue = cellMap.get(category);
								const subCategoryIndex = parentCategory.children.findIndex(subCategory => subCategory.formattedValue === categoryCell.formattedValue);
								parentValue.children[subCategoryIndex] = valueCell;
							}
						}
					});
				}
			});
		});

		const categoriesList = Array.from(
			new Set([].concat(...categoriesPerPortfolio)) // flatten and get unique headers
		)
			.filter(item => !!item)
			.sort((a, b) => this.categorySortFunction?.(a, b) ?? a.localeCompare(b)); // default sort is alphabetical

		// reconstruct the cells grid with defaults for missing entries
		const cells: CellData[][] = portfoliosValues.map(portfolioValues =>
			categoriesList.map(category => {
				let value = portfolioValues.get(category);
				const categoryCell = categoriesUniverse.get(category);
				if (!value) {
					const parent = !categoryCell.parent;
					value = this.extractDataColumn(parent ? this.value : this.childValue, [], contentSvc, categoryCell.parent);
				}

				// condition to fill in missing subCategories when ChildMapperDirection.ChildToParent is passed
				// e.g. for the asset class table when the portfolios have differing subclasses, all subclasses must appear under all portfolios for the table to render correctly
				if (this.childMapperDirection === ChildMapperDirection.ChildToParent) {
					categoryCell.children.forEach(child => {
						// we need to use the same index as seen in the child mapper to place the missing child value in the correct cell
						const subCategoryIndex = categoryCell.children.findIndex(subCategory => subCategory.formattedValue === child.formattedValue);
						if (!value.children[subCategoryIndex]) { // if the parent category was present but missing child or if the parent was entirely missing 
							value.children[subCategoryIndex] = (this.extractDataColumn(this.childValue, [], contentSvc));
						}
					});
				}

				return value;
			})
		);
		// reconstruct the column names
		const columnNames = categoriesList.map(cat => categoriesUniverse.get(cat));

		const tableData = new TableData(columnNames, portfolioNames, cells, portfolioNames, this.translateColumnText(this.firstColumnTitle, contentSvc));
		const transformedCells: TableData = this.tableDataTransform.transform(tableData, contentSvc);

		return transformedCells;
	}
}

export interface CombinedPortfoliosGridExtractorConfigOptions {
	mapper: SeriesMapper;
	firstColumnTitle?: ColumnText;
	portfolioName: ColumnConfig<StoreColumnExtractor>;
	category: ColumnConfig<ColumnExtractor>;
	value: ColumnConfig<ColumnExtractor>;
	categorySortFunction?: (a, b) => number;
	childMapper?: (parent: any) => any[];
	childMapperDirection?: ChildMapperDirection;
	childCategory?: ColumnConfig<ColumnExtractor>;
	childValue?: ColumnConfig<ColumnExtractor>;
	tableDataTransform?: TableDataTransform;
	batchSize?: number;
	portfolioFilter?: (port: any) => boolean;
	firstColumnExtractor?: ColumnConfig<ColumnExtractor>;
}
